/**
 * CoffeeMates Vouchers Service
 */
import httpClient from '../utils/httpClient';

export interface VoucherItem {
  voucher_id: string;
  event_id: string;
  event_title: string;
  event_date: string | null;
  qr_token: string | null;
  short_code: string;
  tier: string;
  amount: number;
  status: string;
  is_floating?: boolean;
  floating_expires_at?: string;
  original_event_id?: string;
  switch_used?: boolean;
  created_at?: string;
  redeemed_at?: string;
}

export interface PurchaseResult {
  message: string;
  voucher_id: string;
  qr_token: string;
  short_code: string;
  status: string;
  amount: number;
  credit_applied: number;
  gateway_charged: number;
  event_title: string;
  event_date: string;
}

// Gateway comes from VITE_PAYMENT_GATEWAY so `mock` never ships to
// production; backend validates server-side too.
const PAYMENT_GATEWAY: string =
  (import.meta.env.VITE_PAYMENT_GATEWAY as string | undefined) ?? 'mock';

const vouchersService = {
  async purchase(eventId: string, tier = 'standard', useCredits = false): Promise<PurchaseResult> {
    const { data } = await httpClient.post<PurchaseResult>('/vouchers/purchase', {
      event_id: eventId,
      tier,
      use_credits: useCredits,
      gateway: PAYMENT_GATEWAY,
    });
    return data;
  },

  async cancel(voucherId: string, reason?: string) {
    const { data } = await httpClient.post(`/vouchers/${voucherId}/cancel`, { reason });
    return data;
  },

  async withdraw(voucherId: string) {
    const { data } = await httpClient.post(`/vouchers/${voucherId}/withdraw`);
    return data;
  },

  async redeemFloating(voucherId: string, eventId: string) {
    const { data } = await httpClient.post(`/vouchers/${voucherId}/redeem-floating`, { event_id: eventId });
    return data;
  },

  async switchEvent(voucherId: string, newEventId: string) {
    const { data } = await httpClient.post(`/vouchers/${voucherId}/switch`, { new_event_id: newEventId });
    return data;
  },

  async getActive(): Promise<{ vouchers: VoucherItem[] }> {
    const { data } = await httpClient.get<{ vouchers: VoucherItem[] }>('/vouchers/my/active');
    return data;
  },

  async getHistory(): Promise<{ vouchers: VoucherItem[] }> {
    const { data } = await httpClient.get<{ vouchers: VoucherItem[] }>('/vouchers/my/history');
    return data;
  },

  async getQR(voucherId: string) {
    const { data } = await httpClient.get(`/vouchers/${voucherId}/qr`);
    return data;
  },

  async getPivotOffers() {
    const { data } = await httpClient.get('/vouchers/my/pivot-offers');
    return data;
  },

  async acceptPivot(voucherId: string, newEventId: string) {
    const { data } = await httpClient.post(`/vouchers/${voucherId}/pivot`, { new_event_id: newEventId });
    return data;
  },

  async declinePivot(voucherId: string) {
    const { data } = await httpClient.post(`/vouchers/${voucherId}/pivot/decline`);
    return data;
  },
};

export default vouchersService;
